import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Eye,
  FileText,
  Globe,
  Lightbulb,
  Loader2,
  Lock,
  Plus,
  Star,
  Target,
  Trash2,
  TrendingUp,
  AlertCircle,
  Zap,
  GraduationCap,
  Copy,
  CheckCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Certification,
  CertificationExamQuestion,
  DifficultyLevel,
  QuestionType,
  certificationService
} from '@/services/certification.service';
import AddQuestionModal from './AddQuestionModal';
import EditQuestionModal from './EditQuestionModal';
import EditCertificationModal from './EditCertificationModal';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';
import PublishConfirmModal from '../ui/PublishConfirmModal';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_DELETE_PERMISSION } from '@/constants/constant-permissions';

interface CertificationDetailsViewProps {
  certification: Certification;
  certificationId: string;
}

export default function CertificationDetailsView({
  certification,
  certificationId
}: CertificationDetailsViewProps) {
  const queryClient = useQueryClient();
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showEditCertificationModal, setShowEditCertificationModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<CertificationExamQuestion | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<CertificationExamQuestion | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [copiedSlug, setCopiedSlug] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Publishing is one-way: a published certification is live and frozen — no
  // settings edit, no question add/edit/delete, and no route back to draft.
  const isPublished = !!certification?.is_published;

  // Deleting a certification is a separate grant from editing it.
  const canDeleteCertification = useHasPermission([CERTIFICATION_DELETE_PERMISSION.CERTIFICATION_DELETE]);

  // Scroll to top when view loads or certification ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [certificationId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStatusDropdown]);

  // Publish/Draft mutation
  const publishMutation = useMutation({
    mutationFn: ({ examId, status }: { examId: string; status: 'PUBLISH' | 'DRAFT' }) =>
      certificationService.publishExam(examId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-details', certificationId] });
      toast.success(`Certification ${certification?.is_published ? 'unpublished' : 'published'} successfully!`);
    },
    onError: (error: any) => {
      // Handle detailed validation errors
      const errorData = error.response?.data;

      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        // Show each validation error
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else if (errorData?.error_description) {
        toast.error(errorData.error_description);
      } else {
        toast.error('Failed to update status');
      }
    },
  });

  // Delete question mutation
  const deleteQuestionMutation = useMutation({
    mutationFn: ({ examId, questionId }: { examId: string; questionId: string }) =>
      certificationService.removeQuestion(examId, questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-details', certificationId] });
      toast.success('Question removed successfully!');
    },
    onError: (error: any) => {
      // Handle detailed validation errors
      const errorData = error.response?.data;

      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        // Show each validation error
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else if (errorData?.error_description) {
        toast.error(errorData.error_description);
      } else {
        toast.error('Failed to remove question');
      }
    },
  });

  const confirmPublish = () => {
    if (!certification) return;
    setShowPublishConfirm(false);
    publishMutation.mutate({ examId: certification._id, status: 'PUBLISH' });
  };

  const handleDeleteQuestion = (questionId: string) => {
    if (!certification) return;

    // Find the question to show in the delete modal
    const question = activeQuestions.find((q: CertificationExamQuestion) => q.id === questionId);
    if (question) {
      setDeletingQuestion(question);
    }
  };

  const confirmDeleteQuestion = () => {
    if (!certification || !deletingQuestion) return;

    deleteQuestionMutation.mutate({
      examId: certification._id,
      questionId: deletingQuestion.id
    });

    // Close modal on success - the mutation's onSuccess will handle invalidation
    setDeletingQuestion(null);
  };

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleExpandAll = () => {
    if (allExpanded) {
      setExpandedQuestions(new Set());
    } else {
      setExpandedQuestions(new Set<string>(activeQuestions.map((q: CertificationExamQuestion) => q.id)));
    }
  };

  const handleCopySlug = async () => {
    if (certification.slug) {
      try {
        await navigator.clipboard.writeText(certification.slug);
        setCopiedSlug(true);
        toast.success('Slug copied to clipboard!');
        setTimeout(() => setCopiedSlug(false), 2000);
      } catch (err) {
        toast.error('Failed to copy slug');
      }
    }
  };

  // Filter out deleted questions and sort by order
  const activeQuestions: CertificationExamQuestion[] = ((certification.questions || []) as CertificationExamQuestion[])
    .filter((q: CertificationExamQuestion) => !(q as any).is_deleted)
    .sort((a: CertificationExamQuestion, b: CertificationExamQuestion) => a.order - b.order);

  // Follows the cards themselves, so expanding each one by hand also flips the button.
  const allExpanded = activeQuestions.length > 0
    && activeQuestions.every((q: CertificationExamQuestion) => expandedQuestions.has(q.id));

  const getDifficultyColor = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'EASY':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'MEDIUM':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'HARD':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getQuestionTypeLabel = (type: QuestionType) => {
    switch (type) {
      case 'SINGLE_CHOICE':
        return 'Single Choice';
      case 'MULTIPLE_CHOICE':
        return 'Multiple Choice';
      case 'TRUE_FALSE':
        return 'True/False';
      case 'TEXT_ANSWER':
        return 'Text Answer';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Back Button */}
        <Link
          to="/admin/certifications"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to Certifications
        </Link>

        {/* Certification Header Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Banner or Gradient Background */}
          <div className="relative h-48 bg-gradient-to-br from-brand-blue-600 via-brand-indigo-600 to-brand-purple-600">
            {certification.banner_image_url ? (
              <img
                src={certification.banner_image_url}
                alt="Certification banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <GraduationCap className="w-20 h-20 text-white/30" />
              </div>
            )}

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-3">
              <button
                onClick={() => window.open(`/admin/certification/preview/${certificationId}`, '_blank')}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20"
                title="Preview Certification"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Preview</span>
              </button>
              {/* Still shown when published — settings open read-only, and the
                  delete action inside has its own permission. */}
              <button
                onClick={() => setShowEditCertificationModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20"
              >
                <Edit className="w-4 h-4" />
                <span className="text-sm">{isPublished ? 'View Settings' : 'Edit Settings'}</span>
              </button>

              {/* Published certifications show a locked badge — publishing is final. */}
              {isPublished && (
                <div
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur-sm text-gray-900 rounded-lg border border-white/20 shadow-md font-semibold"
                  title="This certification is published and can no longer be edited or moved back to draft."
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm mr-1"></div>
                  <span className="text-sm mr-2">Published</span>
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                </div>
              )}

              {/* Custom Status Dropdown */}
              {!isPublished && (
              <div ref={statusDropdownRef} className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={publishMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                    {publishMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                    ) : (
                      <div className={`w-2 h-2 rounded-full ${certification.is_published ? 'bg-green-500' : 'bg-yellow-500'} shadow-sm`}></div>
                    )}
                    <span className="text-sm">{certification.is_published ? 'Published' : 'Draft'}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showStatusDropdown && (
                  <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)}></div>

                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                      {/* Draft Option */}
                      <button
                        onClick={() => setShowStatusDropdown(false)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5 group"
                      >
                        <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></div>
                        <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          Draft
                        </span>
                        {!certification.is_published && (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                      </button>

                      {/* Published Option */}
                      <button
                        onClick={() => {
                          setShowStatusDropdown(false);
                          setShowPublishConfirm(true);
                        }}
                        disabled={publishMutation.isPending}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5 group disabled:opacity-50 disabled:cursor-default"
                      >
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                        <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          Published
                        </span>
                        {certification.is_published && (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Header Content */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Thumbnail */}
              {certification.thumbnail_image_url && (
                <div className="flex-shrink-0">
                  <img
                    src={certification.thumbnail_image_url}
                    alt="Certification thumbnail"
                    className="w-24 h-24 rounded-lg object-cover shadow-md"
                  />
                </div>
              )}

              {/* Title and Description */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-brand-indigo-100 rounded-lg">
                    <Award className="w-6 h-6 text-brand-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 break-words">
                      {certification.title}
                    </h1>
                    {/* Rating Display - Clickable */}
                    <button
                      onClick={() => window.open(`/admin/certification/preview/${certificationId}?tab=reviews`, '_blank')}
                      className="inline-flex items-center gap-1.5 mb-2 px-2 py-1 hover:bg-yellow-50 rounded-md transition-colors cursor-pointer"
                      title="View reviews"
                    >
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-medium text-gray-700">{certification.rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-gray-500">• {certification.review_count || 0} reviews</span>
                    </button>
                    <p className="text-gray-600 leading-relaxed break-words">
                      {certification.description || 'No description available'}
                    </p>
                  </div>
                </div>

                {/* Metadata Section */}
                <div className="space-y-3 mt-4">
                  {/* Slug */}
                  {certification.slug && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[60px] pt-1.5">Slug:</span>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-indigo-50 border border-brand-indigo-200 text-brand-indigo-700 rounded-lg text-sm font-medium">
                        <Globe className="w-4 h-4 flex-shrink-0" />
                        <span className="font-mono break-all">{certification.slug}</span>
                        <button
                          onClick={handleCopySlug}
                          className="ml-1 p-1 hover:bg-brand-indigo-100 rounded transition-colors flex-shrink-0"
                          title="Copy slug"
                        >
                          {copiedSlug ? (
                            <CheckCheck className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Tags  */}
                  {certification.tags && certification.tags.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[60px] pt-1.5">Tags:</span>
                      <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                        {certification.tags.map((tag, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white rounded-2xl text-sm font-medium shadow-sm"
                          >
                            <Target className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="min-w-0 break-all">{tag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-brand-indigo-500">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Total Questions</span>
              <FileText className="w-5 h-5 text-brand-indigo-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{activeQuestions.length}</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-brand-emerald-500">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Total Points</span>
              <TrendingUp className="w-5 h-5 text-brand-emerald-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{certification.total_points || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-brand-amber-500">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Time Limit</span>
              <Clock className="w-5 h-5 text-brand-amber-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {certification.time_limit_minutes ? `${certification.time_limit_minutes}m` : '∞'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-brand-pink-500">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Passing Score</span>
              <Target className="w-5 h-5 text-brand-pink-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{certification.passing_score || 0}%</p>
          </div>
        </div>

        {/* Questions Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-1">
                <Zap className="w-7 h-7 text-brand-yellow-500" />
                Exam Questions
              </h2>
              <p className="text-gray-600">Review and manage your certification exam questions</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {activeQuestions.length > 0 && (
                <button
                  onClick={handleExpandAll}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {allExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Expand All
                    </>
                  )}
                </button>
              )}
              {!isPublished && (
                <button
                  onClick={() => setShowAddQuestionModal(true)}
                  className="btn btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 whitespace-nowrap"
                >
                  <Plus className="w-5 h-5" />
                  Add Question
                </button>
              )}
            </div>
          </div>

          {activeQuestions.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-indigo-100 rounded-full mb-4">
                <FileText className="w-8 h-8 text-brand-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No questions yet</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Start building your certification exam by adding questions. Create a comprehensive assessment for your learners.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeQuestions.map((question: CertificationExamQuestion, index: number) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  expanded={expandedQuestions.has(question.id)}
                  onToggle={() => toggleQuestion(question.id)}
                  onEdit={() => setEditingQuestion(question)}
                  onDelete={() => handleDeleteQuestion(question.id)}
                  getDifficultyColor={getDifficultyColor}
                  getQuestionTypeLabel={getQuestionTypeLabel}
                  isPublished={certification.is_published || false}
                />
              ))}
            </div>
          )}
        </div>

        {/* Add Question Modal */}
        {showAddQuestionModal && (
          <AddQuestionModal
            examId={certification._id}
            nextOrder={activeQuestions.length + 1}
            onClose={() => setShowAddQuestionModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['certification-details', certificationId] });
              setShowAddQuestionModal(false);
            }}
          />
        )}

        {/* Edit Question Modal */}
        {editingQuestion && (
          <EditQuestionModal
            examId={certification._id}
            question={editingQuestion}
            allQuestions={activeQuestions}
            onClose={() => setEditingQuestion(null)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['certification-details', certificationId] });
              setEditingQuestion(null);
            }}
          />
        )}

        {/* Edit Certification Modal */}
        {showEditCertificationModal && (
          <EditCertificationModal
            certification={certification}
            certificationId={certificationId}
            canDelete={canDeleteCertification}
            isPublished={isPublished}
            onClose={() => setShowEditCertificationModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['certification-details', certificationId] });
              setShowEditCertificationModal(false);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={!!deletingQuestion}
          onClose={() => setDeletingQuestion(null)}
          onConfirm={confirmDeleteQuestion}
          title="Delete Question"
          description="Are you sure you want to remove this question from the certification exam question?"
          itemName={deletingQuestion?.question_text}
          isLoading={deleteQuestionMutation.isPending}
          confirmButtonText="Delete Question"
        />

        {/* Publish confirmation — publishing is irreversible. */}
        <PublishConfirmModal
          isOpen={showPublishConfirm}
          onClose={() => setShowPublishConfirm(false)}
          onConfirm={confirmPublish}
          entityLabel="certification"
          itemName={certification.title}
          isPending={publishMutation.isPending}
          lockedActions={[
            'Add, edit or remove questions',
            'Change its settings',
            'Move it back to draft',
          ]}
        />
      </div>
    </div>
  );
}

// Question Card Component
interface QuestionCardProps {
  question: CertificationExamQuestion;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getDifficultyColor: (difficulty: DifficultyLevel) => string;
  getQuestionTypeLabel: (type: QuestionType) => string;
  isPublished?: boolean;
}

function QuestionCard({
  question,
  index,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  getDifficultyColor,
  getQuestionTypeLabel,
  isPublished = false,
}: QuestionCardProps) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow bg-white">
      {/* Question Header - Clickable */}
      <div
        onClick={onToggle}
        className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 border-b border-gray-200 cursor-pointer hover:from-gray-100 hover:to-gray-150 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="inline-flex items-center justify-center px-3 py-1 bg-brand-indigo-600 text-white rounded-lg text-sm font-bold">
                Question {index + 1}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getDifficultyColor(question.difficulty_level)}`}>
                {question.difficulty_level}
              </span>
              <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
                {getQuestionTypeLabel(question.question_type)}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-lg text-sm font-semibold border border-purple-200">
                <Zap className="w-3.5 h-3.5" />
                {question.points} {question.points === 1 ? 'point' : 'points'}
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-900 leading-relaxed">{question.question_text}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Published certifications are frozen — questions can't be changed. */}
            {!isPublished && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="flex-shrink-0 p-2 text-brand-indigo-600 hover:bg-brand-indigo-50 rounded-lg transition-colors"
                  title="Edit question"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete question"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
            <div className="flex-shrink-0">
              {expanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Question Content - Expandable */}
      {expanded && (
        <div className="p-6">
          {/* Options */}
          {question.options && question.options.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Answer Options:</h4>
              <div className="space-y-3">
                {question.options.map((option, optIndex) => (
                  <div
                    key={option.id}
                    className={`group relative p-4 rounded-xl border-2 transition-all ${
                      option.is_correct
                        ? 'bg-emerald-50 border-emerald-400 shadow-sm'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Option Letter */}
                      <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${
                        option.is_correct
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white border-2 border-gray-300 text-gray-700'
                      }`}>
                        {String.fromCharCode(65 + optIndex)}
                      </div>

                      {/* Option Text */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium leading-relaxed ${
                          option.is_correct ? 'text-emerald-900' : 'text-gray-900'
                        }`}>
                          {option.text}
                        </p>
                        {option.explanation && (
                          <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                            <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <span className="italic">{option.explanation}</span>
                          </div>
                        )}
                      </div>

                      {/* Correct Answer Badge */}
                      {option.is_correct && (
                        <div className="flex-shrink-0">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500 text-white rounded-full text-xs font-bold">
                            <Check className="w-3.5 h-3.5" />
                            Correct
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text Answer Keywords */}
          {question.question_type === 'TEXT_ANSWER' && question.answer_keywords && question.answer_keywords.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Answer Keywords:</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  question.case_sensitive
                    ? 'bg-amber-100 text-amber-700 border border-amber-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}>
                  {question.case_sensitive ? 'Case Sensitive' : 'Case Insensitive'}
                </span>
              </div>
              <div className="p-4 bg-emerald-50 border-2 border-emerald-400 rounded-xl">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-wrap gap-2">
                    {question.answer_keywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-medium"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Explanation */}
          {question.explanation && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-500" />
                Explanation:
              </h4>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-gray-800 leading-relaxed">{question.explanation}</p>
              </div>
            </div>
          )}

          {/* Hint */}
          {question.hint && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Hint for Learners:</h4>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-gray-800 italic leading-relaxed">{question.hint}</p>
              </div>
            </div>
          )}

          {/* Metadata Footer */}
          <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-semibold">Source:</span>
              <span className={`px-2 py-1 rounded ${
                question.source === 'AI_GENERATED'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {question.source}
              </span>
            </div>
            {/* {question.topic && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-semibold">Topic:</span>
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{question.topic}</span>
              </div>
            )} */}
            {question.bloom_level && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-semibold">Bloom Level:</span>
                <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded">{question.bloom_level}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
